<template>
    <Teleport to="#popups-container">
        <sl-animation
            v-if="isVisible"
            name="fadeIn"
            easing="ease-out"
            :duration="150"
            :iterations="1"
            play
        >
            <sl-popup
                ref="popup"
                placement="top"
                arrow
                strategy="fixed"
                active
                :anchor="anchorElement"
                @mousemove="clearHideTimeout"
                @mouseleave="startHideTimeout"
            >
                <slot />
            </sl-popup>
        </sl-animation>
    </Teleport>
</template>

<script setup lang="ts">
import "@shoelace-style/shoelace/dist/components/popup/popup.js";
import "@shoelace-style/shoelace/dist/components/animation/animation.js";
import { ref, onMounted, onBeforeUnmount, toRefs, watch } from "vue";
import { waitForElement } from "@/utils";

const props = defineProps<{
    anchor: HTMLElement | string;
}>();

const { anchor } = toRefs(props);

const anchorElement = ref<HTMLElement>();

watch(
    anchor,
    async () => {
        if (typeof anchor.value === "string") {
            anchorElement.value = await waitForElement<HTMLElement>(
                anchor.value
            );
        } else if (anchor.value instanceof HTMLElement) {
            anchorElement.value = anchor.value;
        }
    },
    { immediate: true }
);

const emit = defineEmits<{
    (e: "mouseenter", event: MouseEvent): void;
    (e: "mouseleave", event: MouseEvent): void;
}>();

const isVisible = ref(false);
const hideTimeout = ref<number | null>(null);

const clearHideTimeout = (e: MouseEvent) => {
    if (hideTimeout.value) {
        clearTimeout(hideTimeout.value);
        hideTimeout.value = null;
    }
    if (!isVisible.value) {
        isVisible.value = true;
        emit("mouseenter", e);
    }
};

/**
 * Postpone the hiding as we might be hovering over the
 * other tracked element (anchor or popup).
 */
const startHideTimeout = (e: MouseEvent) => {
    hideTimeout.value = window.setTimeout(() => {
        isVisible.value = false;
        emit("mouseleave", e);
    }, 0);
};

/**
 * Also listen to mouse events on the anchor.
 */
const setupAnchorListeners = () => {
    if (anchorElement.value) {
        anchorElement.value.addEventListener("mousemove", clearHideTimeout);
        anchorElement.value.addEventListener("mouseleave", startHideTimeout);
    }

    return () => {
        if (anchorElement.value) {
            anchorElement.value.removeEventListener(
                "mousemove",
                clearHideTimeout
            );
            anchorElement.value.removeEventListener(
                "mouseleave",
                startHideTimeout
            );
        }
    };
};

let cleanup = () => {};

watch(anchorElement, (newAnchor, oldAnchor) => {
    if (newAnchor !== oldAnchor) {
        cleanup();
        cleanup = setupAnchorListeners();
    }
});

onMounted(() => {
    cleanup = setupAnchorListeners();
});

onBeforeUnmount(() => {
    cleanup();
    if (hideTimeout.value) {
        clearTimeout(hideTimeout.value);
    }
});
</script>
